import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { analyticsApi } from '@/api';
import { Button, Icon, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';

/**
 * Download one of a report's tables as CSV.
 *
 * A menu rather than a single button because a report is several tables and a
 * CSV is one. Offering "Export" with no choice would have to pick one of them
 * silently, and the one somebody wants is the one they are looking at.
 *
 * The current period goes with it. Exporting a different window from the one on
 * screen is the kind of mismatch nobody notices until the numbers are already
 * in a slide deck.
 */
export function ExportMenu({ sheets, days }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const container = useRef(null);

  // A menu that stays open behind the next thing you click is a menu people
  // stop trusting; Escape closes it too, because a keyboard user has no
  // outside to click.
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event) => {
      if (!container.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const download = useMutation({
    mutationFn: (sheet) => analyticsApi.exportReport(sheet, days),
    onSuccess: (filename) => {
      toast.success(`Downloaded ${filename}`);
      setOpen(false);
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not build that export')),
  });

  return (
    <div ref={container} className="relative">
      <Button
        variant="secondary"
        size="sm"
        isLoading={download.isPending}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Icon name="archive" className="size-4" aria-hidden="true" />
        Export
      </Button>

      {open && (
        <div
          role="menu"
          className="border-line bg-surface absolute right-0 z-20 mt-1 w-64 rounded-lg border p-1 shadow-lg"
        >
          {sheets.map((sheet) => (
            <button
              key={sheet.key}
              type="button"
              role="menuitem"
              disabled={download.isPending}
              onClick={() => download.mutate(sheet.key)}
              className="hover:bg-surface-muted text-ink block w-full rounded-md px-3 py-2 text-left text-sm disabled:opacity-50"
            >
              {sheet.label}
              <span className="text-ink-muted block text-xs">{sheet.hint}</span>
            </button>
          ))}
          {/* Said once, here, rather than on every sheet: the window travels
              with the file and the reader should not have to wonder. */}
          <p className="text-ink-muted border-line mt-1 border-t px-3 py-2 text-xs">
            CSV, covering the period shown above. Amounts are written as decimals with their
            currency.
          </p>
        </div>
      )}
    </div>
  );
}
